import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  Divider,
} from '@mui/material';
import { useAppSelector, useAppDispatch } from '@/app/store';
import { setConsolidatedReportModal, handleSnackbar } from '../uiSlice';
import { reportAPI } from '@/features/dooh/report/reportAPI';
import type { ApiError } from '@/shared/utils/axios';
import { ALL_DIMENSIONS, ALL_METRICS } from './constants';

const ConsolidatedReportModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const { open, deviceIds, campaignIds, from, to } = useAppSelector(
    (state: any) => state.ui.consolidatedReportModal
  );

  const [selectedDimensions, setSelectedDimensions] = useState<Set<string>>(
    new Set(ALL_DIMENSIONS)
  );
  const [selectedMetrics, setSelectedMetrics] = useState<Set<string>>(
    new Set(ALL_METRICS)
  );
  const [isDownloading, setIsDownloading] = useState(false);

  const allDimensionsSelected =
    selectedDimensions.size === ALL_DIMENSIONS.length;
  const allMetricsSelected = selectedMetrics.size === ALL_METRICS.length;
  const allSelected = allDimensionsSelected && allMetricsSelected;
  const someSelected = selectedDimensions.size > 0 || selectedMetrics.size > 0;

  const handleClose = useCallback(() => {
    dispatch(setConsolidatedReportModal({ open: false }));
  }, [dispatch]);

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      setSelectedDimensions(new Set());
      setSelectedMetrics(new Set());
    } else {
      setSelectedDimensions(new Set(ALL_DIMENSIONS));
      setSelectedMetrics(new Set(ALL_METRICS));
    }
  }, [allSelected]);

  const handleToggleDimension = useCallback((dim: string) => {
    setSelectedDimensions(prev => {
      const next = new Set(prev);
      next.has(dim) ? next.delete(dim) : next.add(dim);
      return next;
    });
  }, []);

  const handleToggleMetric = useCallback((metric: string) => {
    setSelectedMetrics(prev => {
      const next = new Set(prev);
      next.has(metric) ? next.delete(metric) : next.add(metric);
      return next;
    });
  }, []);

  const handleSelectAllDimensions = useCallback(() => {
    setSelectedDimensions(
      allDimensionsSelected ? new Set() : new Set(ALL_DIMENSIONS)
    );
  }, [allDimensionsSelected]);

  const handleSelectAllMetrics = useCallback(() => {
    setSelectedMetrics(allMetricsSelected ? new Set() : new Set(ALL_METRICS));
  }, [allMetricsSelected]);

  const handleDownload = useCallback(async () => {
    if (!from || !to) return;

    handleClose();
    setIsDownloading(true);

    try {
      const payload = {
        consolidated: true,
        filters: { deviceIds, campaignIds },
        dimensions: Array.from(selectedDimensions),
        metrics: Array.from(selectedMetrics),
        from,
        to,
      };

      const response = await reportAPI.generateReport(payload);
      const resData = response?.data;
      const reportData = (resData as any)?.data ?? resData;
      const rawColumns: string[] = reportData?.columns ?? [];
      const rows: Record<string, any>[] = reportData?.rows ?? [];

      if (!rows.length) {
        throw {
          message: 'No data found for the selected filters and date range.',
        };
      }

      const columns = [...rawColumns].sort((a, b) => {
        if (a === 'Date' && b === 'Hour') return -1;
        if (a === 'Hour' && b === 'Date') return 1;
        return 0;
      });

      const csvRows = [
        columns.join(','),
        ...rows.map((row: Record<string, any>) =>
          columns.map(col => JSON.stringify(row[col] ?? '')).join(',')
        ),
      ];

      const url = window.URL.createObjectURL(
        new Blob([csvRows.join('\n')], { type: 'text/csv' })
      );
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `consolidated_report_${from}_${to}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      dispatch(
        handleSnackbar({
          open: true,
          message: 'Consolidated report downloaded successfully!',
          severity: 'success',
          autoHideDuration: 3000,
        })
      );
    } catch (error: any) {
      const apiError = error as ApiError;
      dispatch(
        handleSnackbar({
          open: true,
          message:
            apiError.message ||
            'Failed to download consolidated report. Please try again.',
          severity: 'error',
          autoHideDuration: 5000,
        })
      );
    } finally {
      setIsDownloading(false);
    }
  }, [
    from,
    to,
    deviceIds,
    campaignIds,
    selectedDimensions,
    selectedMetrics,
    dispatch,
    handleClose,
  ]);

  const isDownloadDisabled =
    isDownloading ||
    (selectedDimensions.size === 0 && selectedMetrics.size === 0);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Download Consolidated Report</DialogTitle>
      <DialogContent dividers sx={{ overflow: 'hidden' }}>
        {/* Select All */}
        <FormControlLabel
          control={
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected && !allSelected}
              onChange={handleSelectAll}
            />
          }
          label={<Typography fontWeight={600}>Select All</Typography>}
        />

        <Divider sx={{ my: 1.5 }} />

        {/* Two-column layout: Dimensions | Metrics */}
        <Box sx={{ display: 'flex', gap: 0 }}>
          {/* Dimensions */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
              Dimensions
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allDimensionsSelected}
                  indeterminate={
                    selectedDimensions.size > 0 && !allDimensionsSelected
                  }
                  onChange={handleSelectAllDimensions}
                  size="small"
                />
              }
              label={<Typography variant="body2">Select All</Typography>}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {ALL_DIMENSIONS.map(dim => (
                <FormControlLabel
                  key={dim}
                  control={
                    <Checkbox
                      checked={selectedDimensions.has(dim)}
                      onChange={() => handleToggleDimension(dim)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body1">{dim}</Typography>}
                />
              ))}
            </Box>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />

          {/* Metrics */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
              Metrics
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allMetricsSelected}
                  indeterminate={
                    selectedMetrics.size > 0 && !allMetricsSelected
                  }
                  onChange={handleSelectAllMetrics}
                  size="small"
                />
              }
              label={<Typography variant="body2">Select All</Typography>}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {ALL_METRICS.map(metric => (
                <FormControlLabel
                  key={metric}
                  control={
                    <Checkbox
                      checked={selectedMetrics.has(metric)}
                      onChange={() => handleToggleMetric(metric)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body1">{metric}</Typography>}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isDownloading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleDownload}
          disabled={isDownloadDisabled}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConsolidatedReportModal;
